import React, { useState , useEffect} from "react";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Sidebar from "./Sidebar";   // import sidebar
import ReportHeader from "./reportComponents/ReportHeader";
import SummaryBox from "./SummaryBox";
import DonutChartCard from "./DonutChartCard";
import OverallDonut from "./OverallDonut";
import RunTestModal from "./testStatus/RunTestModal";
import ScheduleTestModal from "./testStatus/ScheduleTestModal";
import ReportModal from "./reportComponents/ReportModal";
import OverallReadinessSection from "./OverallReadinessSection";
import "../styles/theme.css";
import "../styles/dashboard.css";
import "../styles/ReportDashboard.css";
import "../styles/ReportHistory.css";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../utils/apiHelper";
import { useSidebar } from "../App";
import { categoryLabels } from "../utils/categoryLabels";
import { getReadinessColor } from "../utils/readinessUtils";
import { useUser } from "../contexts/UserContext";
import {
    FaBug,
    FaExclamationTriangle,
    FaExchangeAlt,
    FaFileUpload,
    FaRobot,
    FaBalanceScale,
    FaNetworkWired,
    FaBolt,
    FaClock,
    FaFolderOpen,
    FaUserShield,
    FaHeartbeat,
    FaCreditCard,
    FaLandmark,
    FaShieldAlt,
    FaClipboardCheck,
    FaCertificate,
    FaUserSecret,
} from "react-icons/fa";
import { AiFillSecurityScan } from "react-icons/ai";

// Survives Dashboard unmount when navigating away within the SPA.
let readinessStatsCache = {
  userKey: null,
  allStats: null,
  targetOptions: null,
  selectedTargetId: "",
};

function Dashboard({ onOpenModal }) {
  const { t, i18n } = useTranslation("dashboard");
  const navigate = useNavigate();
  const [showRunTestModal, setShowRunTestModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedProfileIdForSchedule, setSelectedProfileIdForSchedule] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const { sidebarCollapsed } = useSidebar();
  const { user, loading: userLoading } = useUser();
  const locale = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  // Report data state
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState(null);
  const [reportTimestamp, setReportTimestamp] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [profileName, setProfileName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [dashboardDropdownValue, setDashboardDropdownValue] = useState(
    () => readinessStatsCache.selectedTargetId || ""
  );
  const [targetOptions, setTargetOptions] = useState(
    () => readinessStatsCache.targetOptions || []
  ); // { target_id, target_name, target_type }
  const [allStats, setAllStats] = useState(() => readinessStatsCache.allStats || []); // full list from /api/internal/readiness-score/stats/
  const [statsLoading, setStatsLoading] = useState(() => !Array.isArray(readinessStatsCache.allStats));

  // Fetch report data from API
  const fetchReportData = async () => {
    try {
      setReportLoading(true);
      setReportError(null);
      
      // Removed: user_id query parameter - user is obtained from session
      
      const requestOptions = {
        method: "GET",
        credentials: 'include', // Send session cookies
        redirect: "follow"
      };

      const response = await fetch(getApiUrl('/api/internal/readiness-score/getLatestReport/?profile_type=prompts_docs_llm'), requestOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();

      const timestamp = result?.created_at ?? null;
      const profileIdValue = result?.profile_id ?? null;
      const userEmailValue =
        result?.user_details_summary?.email ||
        user?.email ||
        t("notAvailable");
      const companyNameValue = result?.user_details_summary?.company_name || null;
      
      // Use raw result directly as reportData
      setReportData(result);
      setReportTimestamp(timestamp);
      setProfileId(profileIdValue);
      setUserEmail(userEmailValue);
      setCompanyName(companyNameValue);
      
      // Fetch profile information if we have a profile ID
      if (profileIdValue) {
        fetchProfileInfo(profileIdValue);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportError(error.message);
      // Fallback to sample data on error
      setReportData({});
    } finally {
      setReportLoading(false);
    }
  };

  const applyStatsList = (list) => {
    const byId = new Map();
    list.forEach((item) => {
      if (item.target_id != null && !byId.has(item.target_id)) {
        byId.set(item.target_id, {
          target_id: item.target_id,
          target_name: item.target_name || t("targetFallback", { id: item.target_id }),
          target_type: item.target_type,
        });
      }
    });
    const options = Array.from(byId.values());
    setAllStats(list);
    setTargetOptions(options);
    readinessStatsCache = {
      ...readinessStatsCache,
      userKey: user?.email || null,
      allStats: list,
      targetOptions: options,
    };
  };

  // Fetch stats (unique targets) for dashboard dropdown
  const fetchStats = async (forceRefresh = false) => {
    const userKey = user?.email || null;
    if (
      !forceRefresh &&
      readinessStatsCache.userKey === userKey &&
      Array.isArray(readinessStatsCache.allStats)
    ) {
      setAllStats(readinessStatsCache.allStats);
      setTargetOptions(readinessStatsCache.targetOptions || []);
      if (readinessStatsCache.selectedTargetId) {
        setDashboardDropdownValue(readinessStatsCache.selectedTargetId);
      }
      setStatsLoading(false);
      return;
    }

    try {
      setStatsLoading(true);
      const requestOptions = {
        method: "GET",
        credentials: "include",
        redirect: "follow",
      };
      const response = await fetch(getApiUrl("/api/internal/readiness-score/stats/"), requestOptions);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      applyStatsList(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Error fetching stats for dropdown:", error);
      setTargetOptions([]);
      setAllStats([]);
      readinessStatsCache = {
        userKey,
        allStats: null,
        targetOptions: null,
        selectedTargetId: "",
      };
    } finally {
      setStatsLoading(false);
    }
  };

  const handleTargetChange = (targetId) => {
    setDashboardDropdownValue(targetId);
    readinessStatsCache = {
      ...readinessStatsCache,
      selectedTargetId: targetId,
    };
  };

  // Fetch profile information by ID
  const fetchProfileInfo = async (profileIdValue) => {
    if (!profileIdValue) return;
    
    try {
      const response = await fetch(getApiUrl(`/api/internal/profiles-config/getProfileById/${profileIdValue}/`), {
        method: "GET",
        credentials: 'include', // Send session cookies
        redirect: "follow"
      });
      
      if (response.ok) {
        const profileData = await response.json();
        setProfileName(profileData.profile_name);
      } else {
        setProfileName(t("notAvailable"));
      }
    } catch (error) {
      console.error("Error fetching profile info:", error);
      setProfileName(t("notAvailable"));
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return t("notAvailable");
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(locale);
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return t("invalidDate");
    }
  };

  // Calculate summary data
  const calculateSummaryData = (data) => {
    const categoryMap = new Map();
    
    // Group data by category and calculate totals
    data.forEach(item => {
      const category = item.category;
      const scanPassed = item.scanPassed;
      const totalScans = item.totalScans;
      
      if (!categoryMap.has(category)) {
        categoryMap.set(category, { totalPassed: 0, totalScans: 0 });
      }
      
      const categoryData = categoryMap.get(category);
      categoryData.totalPassed += scanPassed;
      categoryData.totalScans += totalScans;
    });
    
    // Convert to summary data format
    const summaryData = [];
    categoryMap.forEach((categoryData, category) => {
      const readiness = categoryData.totalScans > 0 
        ? Math.round((categoryData.totalPassed / categoryData.totalScans) * 100)
        : 0;
      
      summaryData.push({
        solution: category,
        readiness: readiness,
        totalTests: categoryData.totalScans,
        passedTests: categoryData.totalPassed
      });
    });
    
    return summaryData;
  };

  // Category icons mapping
  const categoryIcons = {
    "behavioural_threats": <FaExclamationTriangle className="text-yellow-500 text-xl mr-4" />,
    "input_output_risks": <FaExchangeAlt className="text-blue-500 text-xl mr-4" />,
    "malicious_doc_uploads": <FaFileUpload className="text-pink-500 text-xl mr-4" />,
    "llm_api_mis_use_abuse": <FaRobot className="text-green-500 text-xl mr-4" />,
    "policy_compliance_tests": <FaBalanceScale className="text-purple-500 text-xl mr-4" />,
    "llm_dos": <FaBolt className="text-amber-500 text-xl mr-4" />,
    "mcp_agent": <FaNetworkWired className="text-orange-500 text-xl mr-4" />,
    "rc_gdpr": <FaUserShield className="text-blue-500 text-xl mr-4" />,
    "rc_hipaa": <FaHeartbeat className="text-red-500 text-xl mr-4" />,
    "rc_pci_dss": <FaCreditCard className="text-green-500 text-xl mr-4" />,
    "rc_eu_ai_act": <FaLandmark className="text-indigo-500 text-xl mr-4" />,
    "rc_owasp_llm": <FaShieldAlt className="text-orange-500 text-xl mr-4" />,
    "rc_nist_ai_rmf": <FaClipboardCheck className="text-teal-500 text-xl mr-4" />,
    "rc_soc2": <FaCertificate className="text-purple-500 text-xl mr-4" />,
    "prompt_injection": <FaUserSecret className="text-rose-500 text-xl mr-4" />,
  };

  // Helper to get category key from display label
  const getCategoryKey = (displayLabel) => {
    return Object.keys(categoryLabels).find(key => categoryLabels[key] === displayLabel) || null;
  };

  // Use global readiness utilities

  // Fetch report data on component mount; reuse cached stats when possible
  useEffect(() => {
    fetchReportData();
  }, [user?.email]);

  useEffect(() => {
    if (userLoading) return;
    fetchStats();
  }, [user?.email, userLoading]);

  // Calculate data for summary boxes from score_json
  const currentReportData = reportData || {};
  const reportId = currentReportData?.id || t("notAvailable");
  const scoreJson = currentReportData?.score_json || {};
  const categoriesData = scoreJson.data || {};
  // Ensure all 7 categories appear even if missing in API response
  const data = Object.keys(categoryLabels).map((categoryKey) => {
    const c = categoriesData[categoryKey] || {};
    const readiness = typeof c.readiness === "number" ? Math.round(c.readiness) : 0;
    const label = categoryLabels[categoryKey] || categoryKey;
    return {
      category: label,
      totalScans: c.total_tests || 0,
      scanPassed: c.total_passed || 0,
      readiness,
      failed_tests: c.failed_tests || {},
      sub_cat_readiness: c.sub_cat_readiness || {},
      avg_response_time: c.avg_response_time || 0,
      sub_cat_avg_time: c.sub_cat_avg_time || {}
    };
  });

  const summaryData = calculateSummaryData(data);

  // Use top-level overall_readiness from API
  const overallReadiness = typeof currentReportData?.overall_readiness === "number"
    ? currentReportData.overall_readiness
    : 0;

  const donutData = data
    .filter(c => {
      const key = getCategoryKey(c.category);
      return key && categoriesData[key];
    })
    .map((c, i) => ({
      label: c.category,
      value: c.readiness,
      color: [
        "#ab2c18", // red
        "#c75619", // red-gold mix
        "#daa520", // gold
        "#80a000", // gold-green blend
        "#008000", // green
        "#145d3d"  // dark green/teal
      ][i % 6],
    }));

  // Extract quick_stats from score_json (new schema)
  const rawQuickStats = scoreJson?.quick_stats || {};
  const getOrNA = (v) => (v === undefined || v === null ? t("notAvailable") : v);
  const quickStats = {
    total_tests: getOrNA(rawQuickStats.total_tests),
    total_categories_tested: getOrNA(rawQuickStats.total_categories_tested),
    total_failed_tests: getOrNA(rawQuickStats.total_failed_tests),
    total_high_risk: getOrNA(rawQuickStats.total_high_risk),
    overall_avg_response_time: getOrNA(rawQuickStats.overall_avg_response_time),
  };

  return (
    <div className="theme-whbl">
      <Header />
      <RunTestModal 
        isOpen={showRunTestModal} 
        onClose={() => setShowRunTestModal(false)}
        onTestStarted={(jobId) => {
          // Navigate to TestStatus page with job_id
          navigate("/status", { state: { jobId: jobId } });
        }}
        onScheduleTest={(profileId) => {
          // Open the schedule modal with the selected profile
          setSelectedProfileIdForSchedule(profileId);
          setShowScheduleModal(true);
        }}
      />
      <ScheduleTestModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedProfileIdForSchedule(null);
        }}
        selectedProfileId={selectedProfileIdForSchedule}
        onScheduleCreated={(result) => {
          // Schedule created successfully
          console.log('Schedule created:', result);
        }}
      />
      <div className="container-fluid">
        <div className="main-container">
          
          {/* Sidebar column */}
          <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
            <Sidebar />
          </div>

          {/* Main content column */}
          <main
            className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`}
            style={{ width: "100%", overflowX: "auto" }}
          >
            <div className="dashboard">
              {/* Dashboard title row */}
              <div className="dashboard-header-row mb-4">
                <h1 className="dashboard-title mb-0">{t("title")}</h1>
                <div className="dashboard-header-actions">
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      try {
                        setLoadingReport(true);

                        // Removed: user_id query parameter - user is obtained from session

                        const response = await fetch(getApiUrl('/api/internal/readiness-score/getLatestReport/'), {
                          method: 'GET',
                          credentials: 'include', // Send session cookies
                        });
                        if (!response.ok) {
                          throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const detailedReport = await response.json();
                        setSelectedReport(detailedReport);
                        setShowReportModal(true);
                      } catch (error) {
                        console.error("Error fetching latest report:", error);
                        alert(t("loadLatestReportFailed", { message: error.message }));
                      } finally {
                        setLoadingReport(false);
                      }
                    }}
                    disabled={loadingReport}
                  >
                    {loadingReport ? t("loading") : t("latestReport")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowRunTestModal(true)}
                  >
                    {t("runTests")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("/quick-start")}
                  >
                    {t("quickStart")}
                  </button>
                </div>
              </div>

              <OverallReadinessSection
                targetOptions={targetOptions}
                selectedTargetId={dashboardDropdownValue}
                onTargetChange={handleTargetChange}
                allStats={allStats}
                loading={statsLoading}
                onRefresh={() => fetchStats(true)}
              />

              {/* Recent Summary */}
              <h1 className="dashboard-title mb-0" style={{ marginBottom: "16px" }}>{t("recentSummary")}</h1>
              <ReportHeader
                company={companyName || t("defaultCompany")}
                profile={profileName}
                user={userEmail || t("notAvailable")}
                date={formatTimestamp(reportTimestamp)}
                reportId={reportData?.id || t("notAvailable")}
                profileId={profileId || reportData?.profile_id || t("notAvailable")}
                profileType={reportData?.profile_details_summary?.profile_type || t("notAvailable")}
                userId={reportData?.user_id || t("notAvailable")}
                targetName={reportData?.target_details_summary?.target_name ?? ""}
                overlapPolicy={reportData?.consolidation_details_summary?.overlap_policy}
              />

              {/* Summary Boxes - 4 boxes */}
              <div className="grid grid-4">
                {/* Box 1 - Overall Readiness */}
                <SummaryBox title={t("summary.overallReadiness")}>
                  <OverallDonut title="" value={overallReadiness} />
                  <p style={{ textAlign: "center", color: "var(--body-text)" }}>{t("summary.overallReadinessDesc")}</p>
                </SummaryBox>

                {/* Box 2 - Category Donut */}
                <SummaryBox title={t("summary.categoryWiseReadiness")}>
                  <DonutChartCard title="" data={donutData} />
                </SummaryBox>

                {/* Box 3 - Category Stats */}
                <SummaryBox title={t("summary.categoryDetails")}>
                  <div className="space-y-4">
                    {data.filter(c => {
                      const key = getCategoryKey(c.category);
                      return key && categoriesData[key];
                    }).map((c, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-start">
                          {categoryIcons[getCategoryKey(c.category)] || <FaFolderOpen className="text-gray-500 text-xl mr-4" />}
                          <span className="flex-1 ml-2">
                            {c.category}: <span className="font-bold" style={{ color: getReadinessColor(c.readiness) }}>{c.readiness.toFixed(1)}%</span>
                          </span>
                        </div>
                        <hr className="summary-separator" />
                      </div>
                    ))}
                  </div>
                </SummaryBox>

                {/* Box 4 - Quick Stats */}
                <SummaryBox title={t("summary.quickStats")}>
                  <div className="space-y-4">
                    <div className="flex items-center justify-start">
                      <AiFillSecurityScan className="text-red-600 text-xl mr-4" />
                      <span className="flex-1" style={{ marginLeft: '8px' }}>
                        {t("quickStats.totalScans")} <span className="font-bold">{quickStats.total_tests}</span>
                      </span>
                    </div>
                    <hr className="summary-separator" />

                    <div className="flex items-center justify-start">
                      <FaFolderOpen className="text-pink-500 text-xl mr-4" />
                      <span className="flex-1" style={{ marginLeft: '8px' }}>
                        {t("quickStats.categories")} <span className="font-bold">{quickStats.total_categories_tested}</span>
                      </span>
                    </div>
                    <hr className="summary-separator" />

                    <div className="flex items-center justify-start">
                      <FaExclamationTriangle className="text-red-600 text-xl mr-4" />
                      <span className="flex-1" style={{ marginLeft: '8px' }}>
                        {t("quickStats.highRiskItems")} <span className="font-bold" style={{ color: '#ef4444' }}>{quickStats.total_high_risk}</span>
                      </span>
                    </div>
                    <hr className="summary-separator" />

                    <div className="flex items-center justify-start">
                      <FaBug className="text-red-500 text-xl mr-4" />
                      <span className="flex-1" style={{ marginLeft: '8px' }}>
                        {t("quickStats.failedToBlock")} <span className="font-bold" style={{ color: '#ef4444' }}>{quickStats.total_failed_tests}</span>
                      </span>
                    </div>
                    <hr className="summary-separator" />

                    <div className="flex items-center justify-start">
                      <FaClock className="text-yellow-400 text-xl mr-4" />
                      <span className="flex-1" style={{ marginLeft: '8px' }}>
                        {t("quickStats.avgResponseTime")} <span className="font-bold">{quickStats.overall_avg_response_time}{quickStats.overall_avg_response_time !== t("notAvailable") ? t("quickStats.secondsSuffix") : ""}</span>
                      </span>
                    </div>
                    <hr className="summary-separator" />

                    <div className="flex items-center justify-start">
                      <AiFillSecurityScan className="text-red-600 text-xl mr-4" />
                      <span className="flex-1" style={{ marginLeft: '8px' }}>
                        {t("quickStats.reportId")} <span className="font-bold">{reportId}</span>
                      </span>
                    </div>
                    <hr className="summary-separator" />
                  </div>
                </SummaryBox>
              </div>

              <div className="detail-card panel-default2 padding2">
                  <p>
                    {t("info.intro")}
                  </p>
                  <p>
                    {t("info.deepVerification")}
                  </p>
                  <ul>
                    <li>{t("info.threats.promptInjections")}</li>
                    <li>{t("info.threats.modelDrifts")}</li>
                    <li>{t("info.threats.dataLeakage")}</li>
                    <li>{t("info.threats.maliciousDocs")}</li>
                    <li>{t("info.threats.llmApiMisuse")}</li>
                    <li>{t("info.threats.policyCompliance")}</li>
                  </ul>
                  <p>
                    {t("info.privacyPrefix")}{" "}
                    <a
                      href="https://www.ironsdn.com/WhiteHaXEULA3.pdf"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("info.eulaLink")}
                    </a>
                    {t("info.privacySuffix")}
                  </p>
                </div>
            </div>
          </main>
        </div>
      </div>
      {/* Loading spinner overlay when fetching report details */}
      {loadingReport && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }}>
          <div style={{
            textAlign: "center",
            color: "white"
          }}>
            <div style={{ 
              width: "60px", 
              height: "60px", 
              border: "6px solid rgba(255, 255, 255, 0.3)", 
              borderTop: "6px solid white", 
              borderRadius: "50%", 
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px"
            }}></div>
            <div style={{ fontSize: "18px", fontWeight: "500" }}>
              {t("loadingLatestReport")}
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
      {/* Modal for latest report */}
      <ReportModal 
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setSelectedReport(null);
        }}
        reportData={selectedReport}
        navigate={navigate}
      />
    </div>
  );
}

export default Dashboard;
